import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Trash2, X } from "lucide-react";

import { SessionWhiteboard } from "../../domain/sessionWhiteboard";
import { useSessionWhiteboards } from "../../application/useSessionWhiteboards";
import { Surface, SectionLabel } from "../../../../shared/ui/primitives";

/**
 * The boards the therapist kept from this session's call.
 *
 * Renders nothing at all when none were saved: the board is opt-in, and a
 * permanently empty panel would suggest something is missing when nothing is.
 */
export default function SessionWhiteboards({ sessionId }: { sessionId: string }) {
    const { t, i18n } = useTranslation();
    const { boards, loading, loadFailed, deleteFailed, deletingId, remove, refresh } =
        useSessionWhiteboards(sessionId);
    const [active, setActive] = useState<SessionWhiteboard | null>(null);
    const [retriedUrls, setRetriedUrls] = useState(false);

    const label = (board: SessionWhiteboard) =>
        t("session.whiteboards.item", {
            date: new Date(board.createdAt).toLocaleString(i18n.language),
        });

    // Escape is what anyone reaches for to leave a lightbox, and the browser
    // does nothing for a dialog we render ourselves.
    useEffect(() => {
        if (!active) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") setActive(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active]);

    /**
     * A download URL only lives for minutes. A session left open on screen
     * outlives it, and the first sign is an image that stops loading — so
     * that is where fresh URLs are fetched. Once only, or a genuinely broken
     * image would fetch forever.
     */
    const onImageError = () => {
        if (retriedUrls) return;
        setRetriedUrls(true);
        refresh();
    };

    if (loading) {
        return (
            <Surface padding="lg">
                <div
                    className="flex items-center justify-center"
                    data-testid="whiteboards-loading"
                >
                    <Loader2
                        className="h-5 w-5 animate-spin text-content-tertiary"
                        aria-hidden="true"
                    />
                    <span className="sr-only">{t("common.loading")}</span>
                </div>
            </Surface>
        );
    }

    if (loadFailed && boards.length === 0) {
        return (
            <Surface padding="lg">
                <p role="alert" className="text-sm text-status-critical">
                    {t("session.whiteboards.loadFailed")}
                </p>
            </Surface>
        );
    }

    if (boards.length === 0) return null;

    return (
        <Surface padding="lg">
            <SectionLabel className="mb-4">{t("session.whiteboards.title")}</SectionLabel>

            {deleteFailed && (
                <p role="alert" className="mb-4 text-sm text-status-critical">
                    {t("session.whiteboards.deleteFailed")}
                </p>
            )}

            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {boards.map((board) => (
                    <li
                        key={board.id}
                        className="overflow-hidden rounded-lg border border-border-subtle bg-surface-sunken"
                    >
                        <button
                            type="button"
                            onClick={() => setActive(board)}
                            className="block w-full text-left"
                        >
                            <img
                                src={board.downloadUrl}
                                alt={label(board)}
                                onError={onImageError}
                                className="aspect-square w-full bg-room-paper object-contain"
                                loading="lazy"
                            />
                        </button>
                        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                            <span className="truncate text-xs font-medium text-content-secondary">
                                {label(board)}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(board.id)}
                                disabled={deletingId === board.id}
                                aria-label={t("session.whiteboards.delete")}
                                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-content-secondary transition-colors duration-[--duration-fast] hover:bg-status-critical-tint hover:text-status-critical-on-tint disabled:opacity-50"
                            >
                                {deletingId === board.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                ) : (
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    </li>
                ))}
            </ul>

            {active && createPortal(
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-950/70 p-4 backdrop-blur-sm"
                    onClick={() => setActive(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={label(active)}
                >
                    {/* A dialog whose only exit is clicking the backdrop is a
                        dialog a keyboard cannot leave. Escape is handled above;
                        this is the same way out for everyone else. */}
                    <button
                        type="button"
                        onClick={() => setActive(null)}
                        aria-label={t("common.close")}
                        className="absolute right-4 top-4 flex size-11 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white transition-colors duration-[--duration-fast] hover:bg-white/20"
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <img
                        src={active.downloadUrl}
                        alt={label(active)}
                        onClick={(event) => event.stopPropagation()}
                        className="max-h-full max-w-full rounded-lg bg-room-paper object-contain shadow-modal"
                    />
                </div>,
                document.body,
            )}
        </Surface>
    );
}
